import { useState, useEffect, useCallback } from 'react'
import { CHAT_API_BASE_URL_READ } from '../config'

interface Session {
  id: string
  user_id?: number
  title?: string | null
  status?: string
  company_id?: number | null
  fy_id?: number | null
  started_at?: string
  last_activity_at?: string
}

const SESSION_STORAGE_KEY = 'chatbot_session'

/**
 * Check if a string is a valid UUID
 */
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}

/**
 * Get access token from localStorage
 */
const getAccessToken = (): string | null => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('CognitoIdentityServiceProvider') && key.endsWith('.accessToken')) {
        return localStorage.getItem(key)
      }
    }
  } catch (error) {
    console.error('[ChatBot] Error getting access token:', error)
  }
  return null
}

export function useChatSession() {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasLoadedFromStorage, setHasLoadedFromStorage] = useState(false)

  // One-time cleanup: Clear any invalid sessions on mount (runs before load)
  useEffect(() => {
    const storedSession = localStorage.getItem(SESSION_STORAGE_KEY)
    if (storedSession) {
      try {
        const parsedSession = JSON.parse(storedSession)
        if (parsedSession.id && !isValidUUID(parsedSession.id)) {
          console.warn('[ChatBot] Found invalid session in storage, clearing immediately:', parsedSession.id)
          localStorage.removeItem(SESSION_STORAGE_KEY)
        }
      } catch (err) {
        // Invalid JSON, clear it
        localStorage.removeItem(SESSION_STORAGE_KEY)
      }
    }
  }, [])

  // Load session from localStorage on mount
  useEffect(() => {
    const accessToken = getAccessToken()
    if (accessToken) {
      const storedSession = localStorage.getItem(SESSION_STORAGE_KEY)
      if (storedSession) {
        try {
          const parsedSession = JSON.parse(storedSession)
          // Validate that the session ID is a valid UUID
          if (parsedSession.id && isValidUUID(parsedSession.id)) {
            setSession(parsedSession)
            console.log('[ChatBot] Loaded valid session from storage:', parsedSession.id)
            setHasLoadedFromStorage(true)
          } else {
            // Invalid session ID (likely old local session), clear it immediately
            console.warn('[ChatBot] Invalid session ID in storage (not a UUID), clearing:', parsedSession.id)
            localStorage.removeItem(SESSION_STORAGE_KEY)
            setSession(null) // Explicitly clear the session state
            setHasLoadedFromStorage(true)
            // Don't auto-create here - let the auto-create effect handle it
          }
        } catch (err) {
          console.error('[ChatBot] Failed to parse stored session:', err)
          localStorage.removeItem(SESSION_STORAGE_KEY)
          setSession(null)
          setHasLoadedFromStorage(true)
        }
      } else {
        setHasLoadedFromStorage(true)
      }
    } else {
      setHasLoadedFromStorage(true)
    }
  }, [])

  const createSession = useCallback(async (): Promise<Session | null> => {
    const accessToken = getAccessToken()
    
    if (!accessToken) {
      setError('Please authenticate first')
      return null
    }

    setLoading(true)
    setError(null)

    try {
      // Try to create session via API
      const response = await fetch(`${CHAT_API_BASE_URL_READ}/chat/session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data: Session = await response.json()
        // Validate that the session ID is a valid UUID before saving
        if (data.id && isValidUUID(data.id)) {
          setSession(data)
          localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data))
          console.log('[ChatBot] Created and saved session:', data.id)
          return data
        } else {
          console.error('[ChatBot] Backend returned invalid session ID (not a UUID):', data.id)
          setError('Invalid session format received from server')
          return null
        }
      } else {
        // If session endpoint fails, don't create a local session
        // Instead, log the error and return null
        const errorText = await response.text()
        console.error('[ChatBot] Failed to create session:', response.status, errorText)
        setError(`Failed to create session: ${response.status}`)
        return null
      }
    } catch (err) {
      console.error('[ChatBot] Failed to create session:', err)
      setError('Failed to connect to chat service')
      // Don't create local session - wait for backend to be available
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const clearSession = useCallback(() => {
    setSession(null)
    setError(null)
    localStorage.removeItem(SESSION_STORAGE_KEY)
    console.log('[ChatBot] Cleared session')
  }, [])

  // Auto-create session when authenticated and no session exists
  useEffect(() => {
    const accessToken = getAccessToken()
    if (
      accessToken && 
      !session && 
      !loading && 
      !error &&
      hasLoadedFromStorage
    ) {
      console.log('[ChatBot] Auto-creating session...')
      createSession()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, loading, error, hasLoadedFromStorage]) // Removed createSession to prevent re-runs

  return {
    session,
    loading,
    error,
    createSession,
    clearSession,
    sessionId: session?.id || null,
  }
}


